import React, { useCallback, useEffect } from "react";
import store, { RootState } from "../../store/store";
import StatusDice from "../Dice/StatusDice";
import "./Status.scss";
import { useAppSelector } from "../../hooks/redux";
import { useNavigate } from "react-router-dom";
import { GameStatus } from "../../models/GameStatus";
import { gameActions } from "../Game/gameSlice";
import useGlobalBodyClick from "../../hooks/useGlobalBodyClick";

export default function Status() {
  const gameState = useAppSelector((state: RootState) => state.game);
  const players = store.getState().game.players;
  const navigate = useNavigate();

  const [showHelp, setShowHelp] = React.useState(false);

  const keyPressed = useCallback((event) => {
    switch (event.key) {
      case 'Escape':
        setShowHelp(false);
        break;
      default:
        break;
    }
  }, []);

  useGlobalBodyClick((target: Element) => {
    if (showHelp) {
      setShowHelp(false);
    }
  });

  useEffect(() => {
    document.addEventListener("keydown", keyPressed, false);

    return () => {
      document.removeEventListener("keydown", keyPressed, false);
    };
  }, [keyPressed]);

  function navigateHome() {
    navigate(`/matchmaking`, { replace: true });
  }

  function getMaxTurns(maxTurns: number = 0, maxRounds: number = 0) {

    if (maxTurns) {
      return `/${maxTurns}`;
    }

    if (!maxTurns && !maxRounds) {
      return '';
    }

    return `/${maxRounds * players.length}`;
  }

  const isHost = Boolean(gameState.hostId === gameState.player?.data.playerId
    || (gameState.gameAdmins && gameState.player?.data.playerId && gameState.gameAdmins.includes(gameState.player?.data.playerId)));

  function showLobbySettings() {
    if (isHost) {
      store.dispatch(gameActions.showLobbySettings());
    }
  }

  function getHelpText() {
    const customRulesElement = gameState.settings.customRules && <>
      <div><b>custom rules:</b></div>
      <div className="status_info_help_custom">{gameState.settings.customRules || ''}</div>
    </>;

    switch (gameState.status) {
      case GameStatus.TURN_RESULTS:
        return <>
          <ul>
            <li>If all players have found the storyteller’s image, or if none have found it, then the storyteller doesn’t score any points and everyone else scores 2 points.</li>
            <li>In any other case, the storyteller scores 3 points and so do the players who found his image.</li>
            <li>Each player, <strong>including storyteller</strong>, scores one point for each vote that was placed on their image.</li>
          </ul>
          {customRulesElement}
        </>;
      case GameStatus.SENTENCE:
        return <>
          <ul>
            <li>Storyteller selects a card and comes up with a sentence (phrase, word, symbol) inspired by it.</li>
            <li>The sentence should be vague enough to mislead some players, but clear enough for others to guess the card.</li>
          </ul>
          {customRulesElement}
        </>;
      case GameStatus.MATCH:
        return <>
          <ul>
            <li>Players select a card that best matches the Storyteller's sentence.</li>
            <li>The goal is to trick others into thinking their card is the Storyteller's card.</li>
          </ul>
          {customRulesElement}
        </>;
      case GameStatus.VOTE:
        return <>
          <ul>
            <li>Players have to try to find Storyteller's card</li>
            <li>Storyteller simply votes for the card he liked the most</li>
            <li>Players can leave comments ✎ about any of the cards</li>
          </ul>
          {customRulesElement}
        </>;
      case GameStatus.GAME_SENTENCE_VOTE:
        return <>
          <ul>
            <li>Players vote ❤️ for the sentences they liked</li>
          </ul>
          {customRulesElement}
        </>;
      default:
        return null;
    }
  }

  return (
    <>
      <section className="status">

        <div className="status_profile">

          <div className="status_info">
            <div className="status_info_icon home-icon no-select" onClick={() => navigateHome()}>
              <img src={'home.png'} alt="home" />
            </div>

            {gameState.gameStarted && showHelp && <div className="status_info_help game_tooltip right-aligned" /* onClick={() => setShowHelp(false)} */>
              <div onClick={() => setShowHelp(false)} className="close_button no-select">×</div>
              {getHelpText()}
            </div>}
          </div>
          {gameState.settings.maxScore > 0 &&
            <div className="status_score">
              Win score: <strong>{gameState.settings.maxScore}</strong>
            </div>
          }
        </div>

        <div className="status_label">
          {gameState.error || gameState.status}

          {/* @TODO add game rules/help information for other states as well */}

          {gameState.gameStarted && getHelpText() !== null &&
            <div className="status_info_icon no-select" onClick={() => setShowHelp(!showHelp)}>ⓘ</div>
          }

        </div>
        {gameState.gameStarted &&
          <div className="status_game">

            <div className="status_game_col">
              <StatusDice />
            </div>

            <div className="status_game_col no-user-select" onDoubleClick={() => showLobbySettings()}>
              <div className="status_round">Round: {gameState.roundNumber}{gameState.settings.maxRounds ? `/${gameState.settings.maxRounds}` : ''}</div>
              <div className="status_turn">Turn: {gameState.turnNumber}{getMaxTurns(gameState.settings.maxTurns, gameState.settings.maxRounds)}</div>
              <div className="status_deck_size">Deck: {gameState.settings.deckSize}/{gameState.settings.deckSize + gameState.playedSize}</div>
            </div>

          </div>
        }
      </section>
      <div className="status-progress">
        <div className="status-progress_filled" style={{ width: `${gameState.gameProgress}%` }}></div>
      </div>
    </>
  )
}